/**
 * Bulk MIS API Service
 *
 * Handles API calls for bulk operation monitoring and analytics
 */

import axios from 'axios';
import { httpClient } from './http-client.service';
import type {
  BulkMisAPIResponse,
  GetBulkBatchesParams,
  BulkBatchDetailsAPIResponse,
  GetBatchDetailsParams,
} from '../types/bulk-mis';

class BulkMisAPIService {
  private readonly BASE_PATH = 'api/v1/bulk/mis';

  /**
   * Get paginated list of bulk batches with summary metrics
   *
   * @param params - Query parameters for filtering and pagination
   * @returns Promise with batches, summary, and pagination data
   *
   * @example
   * ```typescript
   * const response = await bulkMisAPI.getBatches({
   *   page: 1,
   *   limit: 50,
   *   sort_by: 'created_at',
   *   sort_order: 'desc',
   *   status: 'COMPLETED',
   *   search: 'FinProServer-json'
   * });
   * ```
   */
  async getBatches(params?: GetBulkBatchesParams) {
    // Build query parameters
    const queryParams = new URLSearchParams();

    const page = params?.page || 1;
    const limit = params?.limit || 50;
    const sortBy = params?.sort_by || 'created_at';
    const sortOrder = params?.sort_order || 'desc';

    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());
    queryParams.append('sort_by', sortBy);
    queryParams.append('sort_order', sortOrder);

    // Add optional filters
    if (params?.status && params.status !== 'all') {
      queryParams.append('status', params.status);
    }
    if (params?.pipeline_source && params.pipeline_source !== 'all') {
      queryParams.append('pipeline_source', params.pipeline_source);
    }
    if (params?.from_date) {
      queryParams.append('from_date', params.from_date);
    }
    if (params?.to_date) {
      queryParams.append('to_date', params.to_date);
    }

    // Add search filter to query params if provided
    if (params?.search) {
      queryParams.append('reference_id', params.search);
    }

    // Add bulk_action filter (default to 'data_fetch' for this dashboard)
    const bulkAction = params?.bulk_action || 'data_fetch';
    queryParams.append('bulk_action', bulkAction);

    // Add batch_type filter if not 'all'
    if (params?.batch_type && params.batch_type !== 'all') {
      queryParams.append('batch_type', params.batch_type);
    }

    const url = `${this.BASE_PATH}/batches?${queryParams.toString()}`;
    const response = await httpClient.get<BulkMisAPIResponse>(url);
    return response.data;
  }

  /**
   * Export individual records for a specific batch
   */
  async exportBatchRecords(params: {
    reference_id: string;
    status?: string;
    search?: string;
    include_balances?: boolean;
    report_type?: string;
    format?: string;
  }): Promise<Blob> {
    const body: Record<string, any> = {
      export_type: 'records',
      reference_id: params.reference_id,
      include_balances: params.include_balances ?? true,
    };

    if (params.status && params.status !== 'all') {
      body.status = params.status;
    }
    if (params.search) {
      body.search = params.search;
    }
    if (params.report_type) {
      body.report_type = params.report_type;
    }
    if (params.format) {
      body.format = params.format;
    }

    const response = await httpClient.post(
      `${this.BASE_PATH}/export`,
      body,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }

  /**
   * Get details and records for a specific batch by reference_id
   *
   * @param params - Parameters including reference_id, pagination, filters, and include_balances
   * @returns Promise with batch details and records
   *
   * @example
   * ```typescript
   * const response = await bulkMisAPI.getBatchDetails({
   *   reference_id: 'FinProServer-json-mixed-configs-1763552569053-986f98',
   *   page: 1,
   *   limit: 100,
   *   include_balances: true
   * });
   * ```
   */
  async getBatchDetails(params: GetBatchDetailsParams) {
    const body: {
      reference_id: string;
      page?: number;
      limit?: number;
      filters?: { status?: string[]; search?: string };
      include_balances?: boolean;
      bulk_action?: string;
    } = {
      reference_id: params.reference_id,
    };

    if (params.bulk_action) body.bulk_action = params.bulk_action;

    if (params.page) body.page = params.page;
    if (params.limit) body.limit = params.limit;

    // Build filters object
    if (params.status && params.status !== 'all') {
      body.filters = { status: [params.status] };
    }

    // Add search to filters if provided
    if (params.search && params.search.trim().length > 0) {
      if (!body.filters) {
        body.filters = {};
      }
      body.filters.search = params.search.trim();
    }

    // Add include_balances flag (default: false)
    body.include_balances = params.include_balances ?? false;

    const response = await httpClient.post<BulkBatchDetailsAPIResponse>(
      `${this.BASE_PATH}/batch/details`,
      body
    );
    return response.data;
  }

  /**
   * Get summary metrics only (without batch list)
   *
   * @param params - Optional filter parameters
   * @returns Promise with summary metrics
   */
  async getSummary(params?: {
    from_date?: string;
    to_date?: string;
    status?: string;
  }) {
    const response = await httpClient.get<{ data: { summary: BulkMisAPIResponse['data']['summary'] } }>(
      `${this.BASE_PATH}/summary`,
      { params }
    );
    return response.data;
  }

  /**
   * Export bulk MIS data with filters
   *
   * @param params - Optional filter parameters (same as getBatches)
   * @returns Promise with blob data (Excel/CSV file)
   *
   * @example
   * ```typescript
   * const blob = await bulkMisAPI.exportData({
   *   status: 'COMPLETED',
   *   from_date: '2024-01-01',
   *   to_date: '2024-12-31'
   * });
   * ```
   */
  async exportData(params?: GetBulkBatchesParams): Promise<Blob> {
    const body: Record<string, any> = {};

    // Add optional filters to request body
    if (params?.status && params.status !== 'all') {
      body.status = params.status;
    }
    if (params?.pipeline_source && params.pipeline_source !== 'all') {
      body.pipeline_source = params.pipeline_source;
    }
    if (params?.from_date) {
      body.from_date = params.from_date;
    }
    if (params?.to_date) {
      body.to_date = params.to_date;
    }
    if (params?.search) {
      body.reference_id = params.search;
    }

    // Add bulk_action filter (default to 'data_fetch' for this dashboard)
    body.bulk_action = params?.bulk_action || 'data_fetch';

    // Add batch_type filter if not 'all'
    if (params?.batch_type && params.batch_type !== 'all') {
      body.batch_type = params.batch_type;
    }

    const response = await httpClient.post(
      `${this.BASE_PATH}/export`,
      body,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }

  /**
   * Download batch file or results
   *
   * @param batchId - The batch ID
   * @param type - Type of download ('input' | 'output' | 'errors')
   * @returns Promise with blob data
   */
  async downloadBatch(
    batchId: string,
    type: 'input' | 'output' | 'errors' = 'output'
  ): Promise<Blob> {
    const response = await httpClient.get(
      `${this.BASE_PATH}/batches/${batchId}/download`,
      {
        params: { type },
        responseType: 'blob',
      }
    );
    return response.data;
  }

  /**
   * Retry a failed batch
   *
   * @param batchId - The batch ID to retry
   * @returns Promise with retry result
   */
  async retryBatch(batchId: string) {
    const response = await httpClient.post<{ data: { message: string } }>(
      `${this.BASE_PATH}/batches/${batchId}/retry`
    );
    return response.data;
  }

  /**
   * Check if a reference ID already exists and get a suggestion for the next available name
   *
   * @param referenceId - The reference ID to check
   * @returns Promise with exists flag and suggested alternative
   */
  async checkReferenceId(referenceId: string): Promise<{ exists: boolean; suggestion: string | null }> {
    const response = await httpClient.post<{ data: { exists: boolean; suggestion: string | null } }>(
      'bulk/checkReferenceId',
      { reference_id: referenceId }
    );
    return response.data?.data || response.data;
  }

  /**
   * Upload a bulk file for processing
   *
   * @param formData - FormData containing the file and metadata
   * @param signal - Optional AbortSignal for cancelling the upload
   * @returns Promise with upload result
   *
   * @example
   * ```typescript
   * const formData = new FormData();
   * formData.append('file', fileObject);
   * formData.append('pipeline_source', 'portal');
   *
   * const controller = new AbortController();
   * const response = await bulkMisAPI.uploadFile(formData, controller.signal, 'bulk/fileUpload/dataFetch');
   * ```
   */
  async uploadFile(formData: FormData, signal?: AbortSignal, endpoint?: string) {
    const payloadEncKey = sessionStorage.getItem('payloadEncKey');

    // Get screenName from formData or use default - extracted to avoid duplication
    const screenName = formData.get('screenName') as string || 'data_fetch';

    // Determine upload endpoint - use provided endpoint or default based on screenName
    const uploadEndpoint = endpoint || this.getUploadEndpoint(screenName);

    // If encryption is enabled, convert to base64 and use httpClient
    if (payloadEncKey === 'true') {
      // Extract values from FormData with runtime type validation
      const file = formData.get('file');
      const reference_id = formData.get('reference_id');
      const global_config = formData.get('global_config');
      
      // Validate file is actually a File object
      if (!file || !(file instanceof File)) {
        throw new Error('File is required and must be a valid File object');
      }

      // Validate reference_id is a string if provided
      if (reference_id !== null && typeof reference_id !== 'string') {
        throw new Error('reference_id must be a string');
      }

      // Validate global_config is a string if provided
      if (global_config !== null && typeof global_config !== 'string') {
        throw new Error('global_config must be a string');
      }

      // Validate file size before base64 conversion to prevent UI freezes
      // 50MB limit (base64 encoding increases size by ~33%, so this prevents memory issues)
      const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`File size exceeds maximum limit of ${MAX_FILE_SIZE / (1024 * 1024)}MB. Please upload a smaller file.`);
      }

      // Convert file to base64
      const base64Content = await this.fileToBase64(file);
      
      // Validate base64 conversion was successful
      if (!base64Content || base64Content.length === 0) {
        throw new Error('Failed to convert file to base64');
      }
      
      // Safely parse global_config JSON
      let parsedGlobalConfig = null;
      if (global_config) {
        try {
          parsedGlobalConfig = JSON.parse(global_config);
        } catch (error) {
          throw new Error('Invalid global_config JSON format');
        }
      }
      
      // Create JSON payload
      const payload = {
        file: {
          name: file.name,
          type: file.type,
          size: file.size,
          content: base64Content
        },
        reference_id: reference_id || null,
        global_config: parsedGlobalConfig,
        screenName: screenName
      };

      // Use httpClient which will encrypt the request
      const response = await httpClient.post(
        uploadEndpoint,
        payload,
        { signal }
      );

      return response.data;
    } else {
      // Legacy: Use axios directly for FormData uploads
      const baseUrl = httpClient.getBaseUrl();
      const token = sessionStorage.getItem('finpro_k_Authorization');

      const response = await axios.post(
        `${baseUrl}${uploadEndpoint}`,
        formData,
        {
          signal,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            // Let axios auto-set Content-Type with boundary for FormData
          },
        }
      );

      // Handle token rotation if present
      const newToken = response.headers['session-token'];
      if (newToken) {
        sessionStorage.setItem('finpro_k_Authorization', newToken);
      }

      return response.data;
    }
  }

  /**
   * Get upload endpoint based on screenName/operation type
   * Endpoints match Angular implementation:
   * - data_fetch -> bulk/fileUpload/dataFetch
   * - consent_request -> bulk/fileUpload/requestConsent (for bulk consent creation)
   * - consent_revoke/revoke_consent -> bulk/fileUpload/revokeConsent
   */
  private getUploadEndpoint(screenName: string): string {
    const endpointMap: Record<string, string> = {
      'data_fetch': 'bulk/fileUpload/dataFetch',
      'consent_request': 'bulk/fileUpload/requestConsent',
      'create_consent': 'bulk/fileUpload/requestConsent',
      'revoke_consent': 'bulk/fileUpload/revokeConsent',
      'consent_revoke': 'bulk/fileUpload/revokeConsent',
    };

    const endpoint = endpointMap[screenName];
    if (!endpoint) {
      console.warn(`[BulkMisAPI] Unknown screenName "${screenName}", falling back to data_fetch endpoint`);
      return 'bulk/fileUpload/dataFetch';
    }
    return endpoint;
  }

  /**
   * Convert file to base64 string
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }
}

export const bulkMisAPI = new BulkMisAPIService();
export { BulkMisAPIService };
